/* eslint-disable camelcase */
import { ReactNode, createContext, useState, useCallback, useMemo, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { authFetcher } from "../hooks/useAuthSWR";
import { Tier } from "../types";

import { useAuth } from "./auth";

interface RawAccountInfo {
  org_tier: Tier;
  last_purchased_at: number | undefined;
  used_mins: number | undefined;
  available_mins: number | undefined;
  is_ultra_low_latency_available: boolean;
}

interface ContextProps {
  tier: Tier;
  lastPurchasedAt: number | undefined;
  usedMins: number | undefined;
  availableMins: number | undefined;
  isUltraLowLatencyAvailable: boolean;
  getAccount: (cb?: (error: any) => void) => Promise<RawAccountInfo>;
}

interface ProviderProps {
  children: ReactNode;
}

const AccountContext = createContext<Partial<ContextProps>>({});

const isPublicPath = (path: string) => path === "/login" || path === "/set-password";

const AccountProvider = ({ children }: ProviderProps): JSX.Element => {
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const reloadAvailableMins =
    getAvailableMinsFromStorage() !== null && getAvailableMinsFromStorage() !== "undefined"
      ? parseInt(getAvailableMinsFromStorage() as string, 10)
      : undefined;
  const reloadTier =
    getTierFromStorage() !== null && getTierFromStorage() !== "undefined" ? (getTierFromStorage() as Tier) : undefined;
  const [tier, setTier] = useState<Tier | undefined>(reloadTier);
  const [lastPurchasedAt, setLastPurchasedAt] = useState<number | undefined>();
  const [usedMins, setUsedMins] = useState<number | undefined>();
  const [availableMins, setAvailableMins] = useState<number | undefined>(reloadAvailableMins);
  const [isUltraLowLatencyAvailable, setIsUltraLowLatencyAvailable] = useState<boolean>(false);

  const getAccount = useCallback(
    async (cb?: (error: any, data?: RawAccountInfo) => void) => {
      try {
        const { data } = await authFetcher("/accounts", {
          method: "get",
          logout,
        });

        setTier(data?.org_tier);
        setLastPurchasedAt(data?.last_purchased_at);
        setUsedMins(data?.used_minutes);
        setAvailableMins(data?.available_minutes);
        setTierToStorage(data?.org_tier);
        setAvailableMinsToStorage(data?.available_minutes);
        setIsUltraLowLatencyAvailable(data?.is_ultra_low_latency_available);
        if (cb) cb(null, data);
        return data;
      } catch (error) {
        console.error("> account > getAccount: ", error);
        if (cb) cb(error);
        throw error;
      }
    },
    [logout]
  );

  useEffect(() => {
    if (!isPublicPath(pathname)) {
      // cannot try/catch promise inside a sync function
      getAccount().catch(() => logout());
    }
  }, [pathname, getAccount, logout]);

  const value = useMemo(
    () => ({
      tier,
      lastPurchasedAt,
      usedMins,
      availableMins,
      isUltraLowLatencyAvailable,
      getAccount,
    }),
    [tier, lastPurchasedAt, usedMins, availableMins, isUltraLowLatencyAvailable, getAccount]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

const useAccount = (): ContextProps => useContext(AccountContext) as ContextProps;

const getTierFromStorage = (): string | null => localStorage.getItem("tier");
const setTierToStorage = (val: Tier | undefined): void => localStorage.setItem("tier", val ?? "undefined");
const removeTier = (): void => localStorage.removeItem("tier");
const getAvailableMinsFromStorage = (): string | null => localStorage.getItem("availableMins");
const setAvailableMinsToStorage = (val: number | undefined): void =>
  localStorage.setItem("availableMins", val === undefined ? "undefined" : val.toString());
const removeAvailableMins = (): void => localStorage.removeItem("availableMins");

export { AccountProvider, useAccount, removeTier, removeAvailableMins };
